import { ChangeEvent, Fragment, ReactElement } from 'react';

import { ChakraText, ChakraVStack, Switch } from '@equip.health/ui';

import { ProviderAccess } from '~/lib/constants/permissions.constants';

import PermissionsTree, {
  OnUpdateRolesParams,
} from '../admin/users/PermissionsTree';

export interface PermissionsBoxProps<P extends string, R extends string> {
  access: ProviderAccess;
  caption?: string;
  isInvalid?: boolean;
  isAccessChecked: boolean;
  label?: string;
  marginBottom?: number | string;
  onUpdateAccess: (access: ProviderAccess, value: boolean) => void;
  onUpdateRoles: (params: OnUpdateRolesParams<P, R>) => void;
  roles: Partial<Record<R, RolesState<P>>>;
  switchSize?: 'sm' | 'md' | 'lg';
}

const PermissionsBox: <P extends string, R extends string>(
  props: PermissionsBoxProps<P, R>,
) => ReactElement<PermissionsBoxProps<P, R>> = <
  P extends string,
  R extends string,
>({
  access,
  caption,
  isInvalid,
  isAccessChecked,
  label,
  onUpdateAccess,
  onUpdateRoles,
  roles,
  switchSize = 'md',
}: PermissionsBoxProps<P, R>) => {
  return (
    <ChakraVStack
      alignItems="flex-start"
      backgroundColor="#00000008"
      border={isInvalid ? '2px solid' : undefined}
      borderColor={isInvalid ? 'error' : undefined}
      borderRadius="16px"
      padding="24px"
      spacing="16px"
    >
      <Switch
        isChecked={isAccessChecked}
        label={label}
        onChange={(evt: ChangeEvent<HTMLInputElement>): void => {
          onUpdateAccess(access, evt.target.checked);
        }}
        size={switchSize}
      />
      <ChakraText color="neutral.secondary" fontSize="10px" lineHeight="140%">
        {caption.split('\\n').map((line: string) => (
          <Fragment key={`PermissionsBox::Fragment::${line}`}>
            {line}
            <br />
          </Fragment>
        ))}
      </ChakraText>
      {isAccessChecked && (
        <PermissionsTree<P, R> onUpdateRoles={onUpdateRoles} roles={roles} />
      )}
    </ChakraVStack>
  );
};

export default PermissionsBox;
